import ExcelJS from "exceljs";
import { Alert, Button, Card, Select, Space, Typography } from "antd";
import { useState } from "react";
import { useNavigate, useParams } from "react-router";
import { Table as ZhTable, type TableColumnProps } from "zhihao-ui-react";

import request, { shippingRequestHttp } from "@/api";
import { UploadExcelFileType, WaterGaugeIndexFileName } from "@/config";
import type { ShipWaterGaugeBatchForm } from "@/api/services/shipWaterGaugeService";
import RequiredMark from "@/views/components/RequiredMark";

export interface ShipWaterGaugeImportRow {
    index: number;
    shipId?: string;
    draft: number | null;
    freeBoard: number | null;
    cargoWeight: number | null;
    errors: string[];
}

export interface ShipCollectorOption {
    value: number;
    label: string;
    employeeId?: string;
}

export interface ShipWaterGaugeBulkUploadPageProps {
    initialRows?: Array<Partial<ShipWaterGaugeImportRow>>;
    collectorId?: number;
    collector?: string;
    loadCollectors?: (keyword: string) => Promise<ShipCollectorOption[]>;
    validateBatch?: (
        rows: ShipWaterGaugeImportRow[],
        collectorId?: number,
        collector?: string
    ) => Promise<ShipWaterGaugeValidationResult>;
    submitBatch?: (rows: ShipWaterGaugeImportRow[], collectorId?: number, collector?: string) => Promise<unknown>;
}

interface ShipWaterGaugeValidationResult {
    errors?: Array<{ index: number; errorMsg: string }>;
}

/** 查询启用状态的收集人，保持 Vue userService.userSelectorByState 的接口契约。 */
export async function loadShipWaterGaugeCollectors(keyword: string): Promise<ShipCollectorOption[]> {
    const response = await request.service.get<
        Array<{ id?: number | string; name?: string; realName?: string; employeeId?: string }>
    >("/admin/user/selectorUser", {
        params: { name: keyword, state: 1 }
    });
    return response.data
        .filter(item => item.id != null)
        .map(item => ({
            value: Number(item.id),
            label: item.name || item.realName || String(item.id),
            employeeId: item.employeeId
        }));
}

/** 校验 Vue 水尺表导入模板的固定表头。 */
export function validateShipWaterGaugeTemplate(headers: string[]): boolean {
    return ["序号", "吃水\n（m）", "干舷\n（m）", "载货量\n（t）"].every(
        (header, index) => headers[index]?.trim() === header
    );
}

/** 将 Excel 行转换为水尺表导入行，并保持 Vue 的数值转换规则。 */
export function buildShipWaterGaugeImportRows(rows: string[][]): ShipWaterGaugeImportRow[] {
    return rows
        .filter(values => values.some(value => value.trim() !== ""))
        .map((values, rowIndex) => {
            const draft = values[1]?.trim() ? Number(values[1]) : null;
            const freeBoard = values[2]?.trim() ? Number(values[2]) : null;
            const cargoWeight = values[3]?.trim() ? Math.round(Number(values[3])) : null;
            const row: ShipWaterGaugeImportRow = {
                index: Number(values[0]) || rowIndex + 1,
                draft: Number.isFinite(draft) ? draft : null,
                freeBoard: Number.isFinite(freeBoard) ? freeBoard : null,
                cargoWeight: Number.isFinite(cargoWeight) ? cargoWeight : null,
                errors: []
            };
            if (row.draft == null) row.errors.push("吃水为空");
            if (row.freeBoard == null) row.errors.push("干舷为空");
            if (row.cargoWeight == null) row.errors.push("载货量为空");
            return row;
        });
}

/** 解析第一张 Excel 表，沿用 Vue 模板校验和 300 条上限。 */
export async function parseShipWaterGaugeWorkbook(file: Blob): Promise<ShipWaterGaugeImportRow[]> {
    const workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(await file.arrayBuffer());
    const worksheet = workbook.worksheets[0];
    const rows: string[][] = [];
    worksheet.eachRow(row => rows.push((row.values as unknown[]).slice(1).map(value => String(value ?? ""))));
    if (!validateShipWaterGaugeTemplate(rows[0] || [])) throw new Error("当前的Excel与模板不一致，请检查!");
    const importRows = buildShipWaterGaugeImportRows(rows.slice(1));
    if (!importRows.length) throw new Error("Excel中无数据，请添加后再上传!");
    if (importRows.length > 300) throw new Error("导入失败，上限导入为300条数据!");
    return importRows;
}

function waterGaugePayload(
    rows: ShipWaterGaugeImportRow[],
    shipId: string,
    collectorId?: number,
    collector?: string
): ShipWaterGaugeBatchForm {
    return {
        shipWaterGaugeImportForms: rows.map(row => ({
            shipId,
            draft: row.draft as number,
            freeBoard: row.freeBoard as number,
            cargoWeight: row.cargoWeight as number
        })) as unknown as ShipWaterGaugeBatchForm["shipWaterGaugeImportForms"],
        collectorId,
        collector
    };
}

/** 调用 Vue 水尺表导入 step=1 预检接口。 */
export async function validateShipWaterGaugeBatch(
    rows: ShipWaterGaugeImportRow[],
    shipId: string,
    collectorId?: number,
    collector?: string
): Promise<ShipWaterGaugeValidationResult> {
    const response = await shippingRequestHttp.service.post(
        "/ship-water-gauge/batchImport/1",
        waterGaugePayload(rows, shipId, collectorId, collector)
    );
    return response.data;
}

/** 调用 Vue 水尺表导入 step=2 提交接口。 */
export async function submitShipWaterGaugeBatch(
    rows: ShipWaterGaugeImportRow[],
    shipId: string,
    collectorId?: number,
    collector?: string
) {
    const response = await shippingRequestHttp.service.post(
        "/ship-water-gauge/batchImport/2",
        waterGaugePayload(rows, shipId, collectorId, collector)
    );
    return response.data;
}

function normalizeRows(rows: Array<Partial<ShipWaterGaugeImportRow>>): ShipWaterGaugeImportRow[] {
    return rows.map((row, index) => ({
        index: row.index || index + 1,
        draft: row.draft ?? null,
        freeBoard: row.freeBoard ?? null,
        cargoWeight: row.cargoWeight ?? null,
        errors: row.errors ? [...row.errors] : []
    }));
}

/** 船舶水尺表导入页，保留 shipId 与收集人字段到 Vue 批量接口。 */
export function ShipWaterGaugeBulkUploadPage({
    initialRows = [],
    collectorId,
    collector,
    loadCollectors = loadShipWaterGaugeCollectors,
    validateBatch,
    submitBatch
}: ShipWaterGaugeBulkUploadPageProps) {
    const { shipId = "" } = useParams();
    const navigate = useNavigate();
    const [rows, setRows] = useState(() => normalizeRows(initialRows));
    const [feedback, setFeedback] = useState("");
    const [selectedCollectorId, setSelectedCollectorId] = useState<number | undefined>(collectorId);
    const [selectedCollector, setSelectedCollector] = useState(collector || "");
    const [collectorOptions, setCollectorOptions] = useState<ShipCollectorOption[]>(
        collectorId == null ? [] : [{ value: collectorId, label: collector || String(collectorId) }]
    );

    /** 远程搜索启用收集人，失败时沿用页面反馈区域提示用户。 */
    const handleCollectorSearch = async (keyword: string) => {
        try {
            setCollectorOptions(await loadCollectors(keyword));
        } catch {
            setFeedback("收集人查询失败");
        }
    };

    /** 读取 Excel 并展示水尺表预览。 */
    const handleFileChange = async (file?: File) => {
        if (!file) return;
        if (!UploadExcelFileType.includes(file.type)) {
            setFeedback("文件须为Excel格式");
            return;
        }
        try {
            setRows(await parseShipWaterGaugeWorkbook(file));
            setFeedback("");
        } catch (error) {
            setFeedback(error instanceof Error ? error.message : "Excel解析失败");
        }
    };

    /** 通过注入回调或 Vue step=1 接口完成服务端预检。 */
    const handleValidate = async () => {
        if (!rows.length) {
            setFeedback("请先上传Excel文件");
            return;
        }
        if (selectedCollectorId == null) {
            setFeedback("请选择收集人");
            return;
        }
        if (rows.some(row => row.errors.length)) {
            setFeedback("数据有误，请修正后再次导入！");
            return;
        }
        try {
            const payloadRows = rows.map(row => ({ ...row, shipId }));
            const result = validateBatch
                ? await validateBatch(payloadRows, selectedCollectorId, selectedCollector)
                : await validateShipWaterGaugeBatch(payloadRows, shipId, selectedCollectorId, selectedCollector);
            const errors = result.errors || [];
            setRows(currentRows =>
                currentRows.map(row => ({
                    ...row,
                    errors: errors.filter(error => error.index === row.index).map(error => error.errorMsg)
                }))
            );
            if (errors.length) setFeedback("数据有误，请修正后再次导入！");
            else setFeedback("数据校验成功");
        } catch {
            setFeedback("导入校验失败");
        }
    };

    /** 仅允许无错误数据进入 Vue step=2 提交。 */
    const handleSubmit = async () => {
        if (!rows.length) {
            setFeedback("请上传正确数据！");
            return;
        }
        if (selectedCollectorId == null) {
            setFeedback("请选择收集人");
            return;
        }
        if (rows.some(row => row.errors.length)) {
            setFeedback(rows.flatMap(row => row.errors).join("，"));
            return;
        }
        try {
            const payloadRows = rows.map(row => ({ ...row, shipId }));
            if (submitBatch) await submitBatch(payloadRows, selectedCollectorId, selectedCollector);
            else await submitShipWaterGaugeBatch(payloadRows, shipId, selectedCollectorId, selectedCollector);
            setFeedback("上传成功！");
            // 沿用 Vue close 行为，水尺批量导入成功后返回来源页面。
            navigate(-1);
        } catch {
            setFeedback("导入失败");
        }
    };

    // 用 zhihao-ui-react 列契约承接 Vue 水尺导入预览的数值和校验状态。
    const columns: TableColumnProps<ShipWaterGaugeImportRow>[] = [
        { key: "index", label: "序号", minWidth: 78, prop: "index" },
        { key: "draft", label: "吃水(米)", minWidth: 120, prop: "draft" },
        { key: "freeBoard", label: "干舷(米)", minWidth: 120, prop: "freeBoard" },
        { key: "cargoWeight", label: "载货量(吨)", minWidth: 120, prop: "cargoWeight" },
        {
            key: "errors",
            label: "数据校验状态",
            minWidth: 300,
            prop: "errors",
            render: ({ row }) => row.errors.join("，") || "数据校验成功"
        }
    ];

    return (
        <section aria-label="ship-water-gauge-bulk-upload-page" className="flex min-h-full flex-col gap-4">
            <Card>
                <Space orientation="vertical" size="middle">
                    <Typography.Title level={4} className="mb-0">
                        水尺表导入
                    </Typography.Title>
                    <Space>
                        <a href={WaterGaugeIndexFileName} download="水尺表导入模板.xlsx">
                            下载模版
                        </a>
                        <span>文件仅限 Excel，单次只支持上传300条数据</span>
                    </Space>
                    <label htmlFor="ship-water-gauge-excel">
                        <RequiredMark />
                        选择Excel文件
                    </label>
                    <input
                        id="ship-water-gauge-excel"
                        aria-label="水尺表Excel文件"
                        type="file"
                        accept={UploadExcelFileType.join(",")}
                        onChange={event => void handleFileChange(event.target.files?.[0])}
                    />
                    <label htmlFor="ship-water-gauge-collector">
                        <RequiredMark />
                        收集人
                    </label>
                    <Select<number, ShipCollectorOption>
                        id="ship-water-gauge-collector"
                        aria-label="收集人"
                        showSearch
                        filterOption={false}
                        allowClear
                        placeholder="请输入姓名"
                        value={selectedCollectorId}
                        options={collectorOptions}
                        onSearch={value => void handleCollectorSearch(value)}
                        onChange={(value, option) => {
                            const selected = Array.isArray(option)
                                ? undefined
                                : (option as ShipCollectorOption | undefined);
                            setSelectedCollectorId(value == null ? undefined : Number(value));
                            setSelectedCollector(selected?.label || "");
                        }}
                    />
                    <span>收集人：{selectedCollector || "未指定"}</span>
                </Space>
            </Card>
            {feedback ? <Alert title={feedback} role="alert" type="info" /> : null}
            <Card>
                <ZhTable<ShipWaterGaugeImportRow>
                    data={rows}
                    columns={columns}
                    pagination={false}
                    rowKey={row => String(row.index)}
                />
            </Card>
            <Space>
                <Button type="primary" onClick={() => void handleValidate()}>
                    校验导入数据
                </Button>
                <Button type="primary" onClick={() => void handleSubmit()}>
                    保存导入
                </Button>
                <Button onClick={() => navigate(-1)}>返回</Button>
            </Space>
        </section>
    );
}

export default ShipWaterGaugeBulkUploadPage;
